import { useCallback, useEffect, useState, type RefObject } from 'react';

/**
 * Tracks whether the element has been scrolled vertically. Returns
 * `[scrolled, refCallback]` — assign `refCallback` to the element's `ref`.
 *
 * Uses a callback ref + `useState` so it correctly handles late-mounting
 * targets (e.g. an element rendered behind a conditional). Pass an external
 * `RefObject` if you also need imperative access to the node; the hook
 * mirrors the node into it.
 */
export function useScrolled<T extends HTMLElement>(
  externalRef?: RefObject<T | null> | null
): readonly [boolean, (node: T | null) => void] {
  const [scrolled, setScrolled] = useState(false);
  const [node, setNode] = useState<T | null>(null);

  const ref = useCallback(
    (n: T | null) => {
      setNode(n);
      if (externalRef) {
        externalRef.current = n;
      }
    },
    [externalRef]
  );

  useEffect(() => {
    if (!node) {
      setScrolled(false);
      return;
    }
    const handle = () => setScrolled(node.scrollTop > 0);
    handle();
    node.addEventListener('scroll', handle, { passive: true });
    return () => node.removeEventListener('scroll', handle);
  }, [node]);

  return [scrolled, ref] as const;
}
